// CANONICAL SOURCE — keep byte-identical with functions/src/lib/reportCadence/computeNextSlot.ts.
// A drift-guard test (src/lib/reportCadence/__tests__/computeNextSlot.test.ts) fails the build
// if the two copies diverge. The module depends only on luxon and the ambient
// ReportSchedules.Cadence type, so it compiles unchanged under both the client (luxon 1)
// and Cloud Functions (luxon 3) — the APIs used here are stable across those majors.
//
// Why a copy and not a shared package: this repo has no monorepo/workspace tooling, the
// client (CRA) and functions build independently with different luxon majors, and a `file:`
// package would pull a second luxon into the client bundle.
import { DateTime } from 'luxon';

// Returns the day-of-month clamped to the target month's length, so a "31st" schedule
// resolves to the last day of shorter months (e.g. 28/29 Feb) rather than overflowing
// into the next month.
export const clampDayOfMonth = ({
  candidate,
  dayOfMonth,
}: {
  candidate: DateTime;
  dayOfMonth: number;
  // daysInMonth is typed `number | undefined` under luxon 3 (undefined only for an
  // invalid DateTime, which cannot occur here); `?? 31` keeps this valid in both majors.
}): DateTime =>
  candidate.set({ day: Math.min(dayOfMonth, candidate.daysInMonth ?? 31) });

// Returns an ISO UTC string for the first cadence-aligned slot strictly after `after`,
// computed in the account timezone. `after` is the anchor — pass the just-delivered slot
// (so the next slot is exact and never drifts with wall-clock processing lag), or `now`
// to seed a brand-new schedule.
const computeNextSlot = ({
  cadence,
  timeZone,
  after,
}: {
  cadence: ReportSchedules.Cadence;
  timeZone: string;
  after: string; // ISO
}): string => {
  const [hour, minute] = cadence.timeOfDay.split(':').map(Number);
  const afterDt = DateTime.fromISO(after).setZone(timeZone);

  let candidate = afterDt.set({ hour, minute, second: 0, millisecond: 0 });

  if (cadence.frequency === 'daily') {
    if (candidate <= afterDt) {
      candidate = candidate.plus({ days: 1 });
    }
  } else if (cadence.frequency === 'weekly') {
    // Luxon weekday: 1 = Monday … 7 = Sunday.
    // Our dayOfWeek: 0 = Sunday, 1 = Monday … 6 = Saturday.
    const luxonDay = cadence.dayOfWeek === 0 ? 7 : cadence.dayOfWeek ?? 1;
    candidate = candidate.set({
      weekday: luxonDay as 1 | 2 | 3 | 4 | 5 | 6 | 7,
    });
    if (candidate <= afterDt) {
      candidate = candidate.plus({ weeks: 1 });
    }
  } else {
    const dayOfMonth = cadence.dayOfMonth ?? 1;
    candidate = clampDayOfMonth({ candidate, dayOfMonth });
    if (candidate <= afterDt) {
      candidate = clampDayOfMonth({
        candidate: candidate.plus({ months: 1 }),
        dayOfMonth,
      });
    }
  }

  return candidate.toUTC().toISO() as string;
};

export default computeNextSlot;
